import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuIconDropdownOption} from '@Components/contextual-menu/components/contextual-menu-icon-dropdown-option';
import type {IconDropdownGroupProps} from '@Components/icon-dropdown-group';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import type {LIST_STYLE, TextListObject} from '@PosterWhiteboard/items/text-item/text-list';
import {LIST_TYPES, ORDERED_LIST_STYLE, UNORDERED_LIST_STYLE} from '@PosterWhiteboard/items/text-item/text-list';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor} from '@Utils/color.util';
import orderedListSvg from '@Assets/postermaker/ordered-bullets.svg';
import unorderedListSvg from '@Assets/postermaker/unordered-bullets.svg';
import styles from './base-text-list-option.module.scss';

interface BaseTextListOptionProps {
  listObject: TextListObject;
  isUnordered: boolean;
  isOrdered: boolean;

  onUpdateListObject(type: LIST_TYPES, style?: LIST_STYLE): void;

  onClickColorObject(): void;
}

export function BaseTextListOption({...props}: BaseTextListOptionProps): ReactElement<any> {
  const updateListObject = (type: LIST_TYPES, style?: LIST_STYLE): void => {
    props.onUpdateListObject(type, style);
  };

  const toggleList = (type: LIST_TYPES): void => {
    if (type === props.listObject.type) {
      updateListObject(LIST_TYPES.NONE);
      return;
    }
    updateListObject(type);
  };

  const onClickBullet = (style: LIST_STYLE, type: LIST_TYPES): void => {
    if (props.listObject.type === type && props.listObject.style === style) {
      updateListObject(LIST_TYPES.NONE);
      return;
    }

    updateListObject(type, style);
  };

  const getUnorderedBullet = (id: LIST_STYLE, className: string): ReactElement<any> => {
    return (
      <li
        id={String(id)}
        onClick={(): void => {
          onClickBullet(id, LIST_TYPES.UNORDERED);
        }}
        className={`${styles.bulletButton} ${id === props.listObject.style && props.isUnordered ? styles.selected : ''} flex-center spacing-p-b-0 radius-2`}
      >
        <img src={unorderedListSvg as string} alt="svg" className={`${className} ${styles.bulletImage} `} />
      </li>
    );
  };

  const getUnorderedBullets = (): ReactElement<any> => {
    return (
      <>
        {getUnorderedBullet(UNORDERED_LIST_STYLE.CIRCLE_1, styles.bulletCircle1)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.CIRCLE_2, styles.bulletCircle2)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.CIRCLE_3, styles.bulletCircle3)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.SQUARE_1, styles.bulletSquare1)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.SQUARE_2, styles.bulletSquare2)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.SQUARE_3, styles.bulletSquare3)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.SQUARE_4, styles.bulletSquare4)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.ARROW_1, styles.bulletArrowOne)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.ARROW_2, styles.bulletArrow2)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.ARROW_3, styles.bulletArrow3)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.TICK_1, styles.bulletTick1)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.TICK_2, styles.bulletTick2)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.STAR, styles.bulletStar)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.EDIT, styles.bulletEdit)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.FLOWER, styles.bulletFlower)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.AESTERISK, styles.bulletAesterisk)}
        {getUnorderedBullet(UNORDERED_LIST_STYLE.LEAF, styles.bulletLeaf)}
      </>
    );
  };

  const getUnorderedBulletsPopup = (): ReactElement<any> => {
    return (
      <div className={`${styles.unorderedContainer} `}>
        <ul className={`${styles.bulletsContainer} ${styles.smallGap} flexbox spacing-m-0`}>{getUnorderedBullets()}</ul>
      </div>
    );
  };

  const orderedListItem = (id: LIST_STYLE, className: string): ReactElement<any> => {
    return (
      <li
        id={String(id)}
        onClick={(): void => {
          onClickBullet(id, LIST_TYPES.ORDERED);
        }}
        className={`${styles.orderedListButton} ${id === props.listObject.style && props.isOrdered ? styles.selected : ''} flex-center spacing-p-b-0 radius-2`}
      >
        <img src={orderedListSvg as string} alt="svg" className={`${className} ${styles.orderedBulletImage}`} />
      </li>
    );
  };

  const getOrderedBullets = (): ReactElement<any> => {
    return (
      <>
        {orderedListItem(ORDERED_LIST_STYLE.NUMBERED_DECIMAL, styles.numberedListDecimal)}
        {orderedListItem(ORDERED_LIST_STYLE.NUMBERED, styles.numberedListNumbers)}
        {orderedListItem(ORDERED_LIST_STYLE.ALPHA_UPPER, styles.upperAlpha)}
        {orderedListItem(ORDERED_LIST_STYLE.ALPHA_LOWER, styles.lowerAlpha)}
        {orderedListItem(ORDERED_LIST_STYLE.ROMAN_UPPER, styles.upperRoman)}
        {orderedListItem(ORDERED_LIST_STYLE.ROMAN_LOWER, styles.lowerRoman)}
      </>
    );
  };

  const getOrderedBulletsPopup = (): ReactElement<any> => {
    return (
      <div className={`${styles.orderedContainer} `}>
        <ul className={`${styles.bulletsContainer} flexbox spacing-m-0`}>{getOrderedBullets()}</ul>
      </div>
    );
  };

  const getBulletOption = (): IconDropdownGroupProps => {
    return {
      id: 'bullet-option',
      icon: 'icon-list',
      dropdownPopup: getUnorderedBulletsPopup(),
      alignment: ALIGNMENT_TYPE.BOTTOM,
      isIconSelected: props.isUnordered,
      onClickIcon: (): void => {
        toggleList(LIST_TYPES.UNORDERED);
      },
      leftOffset: -20,
    };
  };

  const getListOption = (): IconDropdownGroupProps => {
    return {
      id: 'list-option',
      icon: 'icon-list-ordered',
      dropdownPopup: getOrderedBulletsPopup(),
      isIconSelected: props.isOrdered,
      onClickIcon: (): void => {
        toggleList(LIST_TYPES.ORDERED);
      },
    };
  };

  const getData = (): Array<IconDropdownGroupProps> => {
    return [getBulletOption(), getListOption()];
  };

  const getColorData = (): Array<ColorFillStorage> => {
    return [
      {
        id: 'text-list-color-object',
        fillType: getColorFillTypeFromFillType(props.listObject.fill.fillType),
        color: getColorObjectArrayFromFillColor(props.listObject.fill.fillColor),
      },
    ];
  };

  const onClickHandler = (): void => {
    props.onClickColorObject();
  };

  const getSubOption = (): ReactElement<any> | null => {
    if (props.listObject.type === LIST_TYPES.NONE) {
      return null;
    }
    return <ContextualMenuColorFillOption id="text-list-color-option" label={window.i18next.t('pmwjs_color')} data={getColorData()} onClick={onClickHandler} />;
  };

  return (
    <ContextualMenuIconDropdownOption id="text-list-item" label={window.i18next.t('pmwjs_list')} data={getData()}>
      {getSubOption()}
    </ContextualMenuIconDropdownOption>
  );
}
